/* **The dialog surface** — the box every modal in this app that asks a question is drawn in, and the
 * answers along its foot.
 *
 * A stylesheet with no component, the shape `emberButton.module.css`, `photoBox.module.css` and
 * `segmented.module.css` already take: there is no behaviour here, and the three surfaces that wear
 * it hang it on different components — `shared/ui/ConfirmDialog`, `assigned-plan-calendar`'s
 * `ActDialog` and `pages/account`'s `EraseAccountDialog`, which is a Base UI `AlertDialog` of its own
 * because a typed gate and two lists of consequences are not props a shared confirm should grow.
 *
 * Promoted out of `shared/ui/ConfirmDialog.module.css` on 18 September 2026, on its THIRD consumer:
 * the erase-account card had re-declared the backdrop, the popup, the title, the description, the
 * actions row, the button box and all three of its states, declaration for declaration. Every rule
 * below is that file's, moved unchanged.
 *
 * ── The WIDTH is the caller's, and it is a custom property rather than a second rule ─────────
 * A confirm is 520 and the erase card is 580, because two columns of consequences do not fit 520.
 * `--dialog-w` is the hook and this file only READS it — so exactly one stylesheet ever declares a
 * width for a given popup, and chunk order, which differs between the dev server and the build, has
 * nothing to settle. Redeclaring `width` on a second single-class selector is the failure that rule
 * exists for.
 *
 * ── Nothing is declared on both sides ───────────────────────────────────────────────────────
 * A surface wearing these classes adds its own content blocks and restates none of what is here. */

.backdrop {
  position: fixed;
  inset: var(--space-0);
  z-index: 30;
  background: var(--scrim);
}

/* ── The height bound is a safety net, not routine layout ────────────────────────────────────
 * Measured, this card is about 210px — a title, two or three lines, and a button row — and roughly
 * 280px with a long subject on its own line. At 1280×620 the bound resolves to 572px, so nothing
 * here reaches it and the dialog looks exactly as it did. It engages only below about a 340px
 * viewport, which is under anything this app supports.
 *
 * It is here anyway because the failure without it has no floor. The popup is `position: fixed` and
 * centred by transform, so a card taller than the window overhangs BOTH edges at once, and being
 * fixed it is not in the document's scroll flow — the page cannot scroll to it, and neither can
 * anything else. That is a clip with no scroll path, which `CLAUDE.md` rules out outright. A
 * description that grows — a long client name, a translation, a prompt written later — should meet a
 * scrollbar rather than silently put "Anuluj" off the bottom of the screen.
 *
 * The scroll goes on the POPUP rather than on the description, and that is the deliberate half. The
 * popup is the dialog: it takes focus when it opens, so it is keyboard-scrollable as it stands,
 * where an overflowing `.description` between two pinned rows would be a scroll container with no
 * focusable element inside it and no way to reach by keyboard alone — WCAG 2.1.1 — which the repo
 * has already run into (`PlanLibraryTable.module.css`: a div with neither `tabindex` nor a role is
 * not focusable). Pinning the buttons would read better in the case that never happens, at the cost
 * of a permanent extra tab stop on every confirm in the app. */
.popup {
  position: fixed;
  top: 50%;
  left: 50%;
  z-index: 31;
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  width: min(var(--dialog-w, 520px), calc(100vw - 48px));
  max-height: var(--overlay-max-block);
  padding: var(--space-6);
  overflow-y: auto;
  overscroll-behavior: contain;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-pop);
  transform: translate(-50%, -50%);
}

.title {
  font-size: var(--fs-h3);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-title);
  color: var(--text);
}

.description {
  font-size: var(--fs-label);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-relaxed);
  color: var(--text-2);
}

.actions {
  display: flex;
  gap: var(--space-2);
  justify-content: flex-end;
}

.cancel,
.ghost,
.dangerGhost,
.primary,
.danger {
  height: var(--h-control);
  padding: var(--space-0) var(--space-4);
  font-size: var(--fs-label);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-heading);

  /* Split rather than a shorthand: the allowlist requires a TOKEN colour in `border`, and the base
   * edge here is transparent so the two buttons reserve the same box whichever one paints one. */
  border-width: var(--border-hairline);
  border-style: solid;
  border-color: transparent;
  border-radius: var(--r-button);
}

/* ── Cancel is not an answer, and stopped being drawn as one ──────────────────────────────────
 * Every dialog in this app put "Anuluj" and the middle answer side by side as byte-identical
 * bordered ghosts. On the removal's scope question that was already fixed by hand — the alternate
 * took the danger outline — but the other four were still asking the trainer to tell two identical
 * pills apart by reading them, and on the square's own choice the pill on the LEFT was `Anuluj`
 * while the one beside it was `Dodaj jednorazowy`, an answer with no obvious way back. The eye
 * reaches for the nearer of two identical shapes.
 *
 * So Cancel loses the edge entirely and leaves the answer stack: plain ink on nothing, pushed to the
 * far side by `margin-right: auto`. It is one rule for every dialog `ConfirmDialog` draws rather
 * than a treatment per caller, which is what stops the next one arriving as its own arrangement. The
 * box is still the shared one above, so the row's controls line up and the reserved transparent edge
 * keeps them the same height.
 *
 * `--text-2` rather than `--text`: the emphasis order the card needs is retreat, then the marked
 * answer, then the loudest — and this is the only one of the three that is not an act. It measures
 * 6.63:1 in Flame and 8.96:1 in Flame Dark against `--bg-card`, well clear of the 4.5:1 SC 1.4.3
 * asks of it. Losing the edge costs nothing under SC 1.4.11 either: the words ARE the control now,
 * so there is no non-text boundary left for it to be measured on.
 *
 * `pages/assigned-plan-calendar`'s `ActDialog` borrows this sheet and still takes `.ghost` for its
 * own Cancel, deliberately: it has TWO answers, so there is no twin to tell apart, and on a bare
 * confirmation Cancel is the control that HOLDS focus — taking its edge away there would leave the
 * focused control the faintest thing on the card. Reconciling the two is the owner's call, not a
 * side effect of this one. */
.cancel {
  margin-right: auto;
  color: var(--text-2);
  background: transparent;
}

.ghost {
  color: var(--text);
  background: transparent;
  border-color: var(--border-2);
}

/* ── The answer that destroys LESS ────────────────────────────────────────────────────────────
 * Set only where the third answer is destructive in its own right — the removal's scope question,
 * where "Tylko ten tydzień" and "Ten tydzień i wszystkie kolejne" both take a training out of the
 * plan and differ only in how far. Drawn as `.ghost` it read as the safe way out, and the SAME words
 * arrive as a filled `--danger` confirm whenever that scope is the only legal one — the same act
 * wearing two opposite treatments one dialog apart.
 *
 * The edge and the ink, no fill: `--danger-text-strong` is the ink authored to carry `--danger`'s
 * meaning as WORDS (`--danger` itself is a non-text value, which is why the filled confirm draws
 * `--on-danger` on top of it rather than the other way round). That gives the three answers an
 * order a glance can read — neutral, marked, loudest — without a second red block on the card.
 *
 * Flat, hairline, no shadow. A coloured blur is banned outright and a danger one is no exception. */
.dangerGhost {
  color: var(--danger-text-strong);
  background: transparent;
  border-color: var(--danger);
}


/* The ink every other ember primary in this app wears. `--accent-grad` is the SAME fill in Flame and
 * Flame Dark, so its foreground cannot be theme-dependent either — and `--text` is one: #111113 on
 * light, #ffffff on dark. Reading the label off it fixed the light theme and left the dark one at
 * the white it started at, by accident rather than by decision.
 *
 * `--on-accent-grad` is that decision, made once: white, by the owner's directive of 5 August 2026,
 * which reversed this app's own contrast correction. The measurement that argued for near-black is
 * recorded in `tokens/colors.css` beside the token and stands; the directive outranks it, and the
 * comment this replaced was still arguing the withdrawn side. */
.primary {
  color: var(--on-accent-grad);
  background: var(--accent-grad);
}

/* ── The answer that DESTROYS, drawn as the answer that destroys ──────────────────────────────
 * An earlier revision moved the EMBER to Cancel and left this one a pale outlined pill, on the
 * argument that "a filled danger button is the loudest thing a screen can carry". Measured, that
 * inverted the dialog: the accent — this app's scalpel, reserved for a real action — landed on
 * "Anuluj", i.e. on doing nothing, while "Zakończ współpracę" was drawn in an edge at 30 % alpha
 * measuring 1.4:1 against the card. SC 1.4.11 wants 3:1 on a control boundary, so the one control
 * that ends a client relationship was the hardest one on screen to find.
 *
 * So: Cancel is NEUTRAL (`.ghost`, the same outline every secondary in this app wears), and the
 * destructive confirm is a solid `--danger` fill. `--on-danger` is the near-black authored for
 * exactly this — 6.13:1 on #ff5a4d, against white's 3.08:1 — and using `--danger` as a FILL under
 * ink is inside its stated "non-text use only" contract; what that contract forbids is drawing
 * WORDS in it, which nothing does.
 *
 * **Focus still lands on Cancel**, which is the whole safety property: Cancel is first in the DOM,
 * so a trainer pressing Enter on reflex does not commit the consequence. Emphasis marks WHAT the
 * button does; focus decides what a reflex does. They are different questions and this is the only
 * arrangement that answers both.
 *
 * Flat fill, hairline edge, no shadow and no glow — the ban is absolute and a danger blur would
 * break it exactly as an ember one would. */
.danger {
  color: var(--on-danger);
  background: var(--danger);
  border-color: var(--danger);
}

/* Declared before the hover below it: `:hover:not([attr])` carries one more compound than
 * `:focus-visible`, and Stylelint's `no-descending-specificity` reads the reverse order as the
 * accident it usually is. */
.cancel:focus-visible,
.ghost:focus-visible,
.dangerGhost:focus-visible,
.danger:focus-visible,
.primary:focus-visible {
  outline: var(--border-focus) solid var(--accent);
  outline-offset: var(--space-2px);
}

/* Cancel has no edge to light up, so its hover is the tint alone — the same `--bg-chip` the app's
 * quiet controls take — plus the primary ink, which is what says the words are a control at all. */
.cancel:hover:not([aria-disabled='true']) {
  color: var(--text);
  background: var(--bg-chip);
}

/* The soft tint, so the outlined answer answers a hover the way the filled one does. It sits AFTER
 * the focus block rather than beside its own base rule, which is the order
 * `no-descending-specificity` reads — `:hover:not([attr])` carries one more compound than
 * `:focus-visible`. */
.dangerGhost:hover:not([aria-disabled='true']) {
  background: var(--danger-soft);
}

/* `--cta-press` is the neutral `brightness()` the ember primary uses, so the two filled buttons in
 * this app darken by the same amount and neither swaps its `background` — a background-color and a
 * background-image cannot interpolate, and the ember's own note records the flicker that causes. */
.danger:hover:not([aria-disabled='true']) {
  filter: var(--cta-press);
}

/* An answer that cannot be taken while an act is in flight. `aria-disabled` rather than the
 * attribute, so it keeps its place in the tab order and the label stays reachable (view ADR §12).
 *
 * The NEUTRAL chip treatment rather than an opacity, which is the mistake `AssignPlanDialog` records
 * measuring: 0.4 over the ember gradient composited its label to roughly 1.3:1, so the one state
 * that owes the trainer an explanation was the one they could not read.
 *
 * ── The ink is `--text-3`, and it had to stop being `--text-2` ───────────────────────────────
 * `--text-2` is this card's BODY ink — the description is drawn in it — and it is also the live
 * `.cancel`. An inert confirm wearing it therefore arrived at full body weight: measured in Flame
 * Dark, #b6b6bd on the chip's #1e1e21 is 9.35:1, LOUDER than anything else in the actions row, so
 * the one answer that does nothing read as the live primary. Light hid it, because there
 * `--text-2`'s #5c5c63 already sits under `.ghost`'s near-black — the same declaration, opposite
 * readings, which is exactly what a state that must rank BELOW its neighbour cannot afford.
 *
 * `--text-3` is the tier authored for inert ink and it ranks under `--text-2` in both themes by
 * construction, so the order holds without a per-theme rule. It is a disabled control, so SC 1.4.3
 * does not apply to it; the reason it cannot be pressed is never carried by this label anyway — it
 * is a rendered sentence the button names through `aria-describedby`.
 *
 * `shared/ui/emberButton.module.css` still gives a refused primary `--text-2`. Left alone
 * deliberately: an ember button stands on the page with no neighbour to out-rank, and re-inking
 * every one of them is a change nobody asked for. */
.cancel[aria-disabled='true'],
.ghost[aria-disabled='true'],
.dangerGhost[aria-disabled='true'],
.danger[aria-disabled='true'],
.primary[aria-disabled='true'] {
  cursor: default;
  color: var(--text-3);
  background: var(--bg-chip);
  border-color: var(--border);
}
